import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import PlansActions, { PlansSelectors } from "Redux/PlansRedux";
import { CreditSelectors } from "Redux/CreditRedux";
import Spinner from "../components/Spinner";
import CreditActions from "Redux/CreditRedux";
import { useToast } from "../components/Toast";

export default function PlansPage() {
  const dispatch = useDispatch();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  
  // Redux state
  const plans = useSelector(PlansSelectors.getPlans);
  const currentPlan = useSelector(PlansSelectors.getCurrentPlan);
  const isFetching = useSelector(PlansSelectors.isFetching);
  const error = useSelector(PlansSelectors.getError);
  const credits = useSelector(CreditSelectors.getCredits);
  const isSubscribing = useSelector(PlansSelectors.isSubscribing);
  const subscribeError = useSelector(PlansSelectors.getSubscribeError);
  const isDowngrading = useSelector(PlansSelectors.isDowngrading);
  const downgradeError = useSelector(PlansSelectors.getDowngradeError);
  const isReactivating = useSelector(PlansSelectors.isReactivating);
  const subscriptionStatus = useSelector(PlansSelectors.getSubscriptionStatus);
  const subscriptionEndsAt = useSelector(PlansSelectors.getSubscriptionEndsAt);
  const isSubscriptionCanceling = useSelector(PlansSelectors.isSubscriptionCanceling);
  
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [successBanner, setSuccessBanner] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');

  // Fetch plans on mount
  useEffect(() => {
    dispatch(PlansActions.plansFetchRequest());
    dispatch(PlansActions.plansCurrentRequest());
    dispatch(CreditActions.creditsFetchRequest());
  }, [dispatch]);

  // Handle return from Stripe checkout
  useEffect(() => {
    const status = searchParams.get('status');
    if (status === 'topup_success') {
      setSuccessBanner('Credits purchased successfully! Your top-up credits have been added.');
      toast.success('Credits added to your account!');
      dispatch(CreditActions.creditsFetchRequest());
    } else if (status === 'success') {
      setSuccessBanner('Subscription activated! Welcome to Creator Pro.');
      toast.success('Welcome to Creator Pro!');
      // Refresh plan data — webhook may not have fired yet, so poll a few times
      const refreshPlanData = () => {
        dispatch(PlansActions.plansCurrentRequest());
        dispatch(PlansActions.plansFetchRequest());
        dispatch(CreditActions.creditsFetchRequest());
      };
      refreshPlanData();
      // Retry after 2s and 5s in case webhook hasn't processed yet
      const t1 = setTimeout(refreshPlanData, 2000);
      const t2 = setTimeout(refreshPlanData, 5000);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [searchParams, dispatch]);

  // Use fallback plans if API fails
  const displayPlans = plans.length > 0 ? plans : (error ? FALLBACK_PLANS : []);
  const creatorProPlan = displayPlans.find(p => p.key === 'creator_pro') || FALLBACK_PLANS[1];
  const isCurrentPlan = currentPlan === 'creator_pro';

  const availableCredits = credits?.available || 0;
  const monthlyCredits = creatorProPlan?.monthly_credits || 200;
  const usagePercent = monthlyCredits > 0 ? Math.round((availableCredits / monthlyCredits) * 100) : 0;

  const formattedEndsAt = useMemo(() => {
    if (!subscriptionEndsAt) return null;
    return new Date(subscriptionEndsAt).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric'
    });
  }, [subscriptionEndsAt]);

  const handleSubscribeClick = () => {
    dispatch(PlansActions.plansSubscribeRequest(window.location.origin, billingCycle));
  };

  const monthlyPrice = billingCycle === 'annual' ? Math.round(290 / 12) : 29;
  const totalPrice = billingCycle === 'annual' ? 290 : 29;

  const handleDowngradeClick = () => {
    setShowDowngradeModal(true);
  };

  const handleConfirmDowngrade = () => {
    dispatch(PlansActions.plansDowngradeRequest());
    setShowDowngradeModal(false);
  };

  const handleReactivate = () => {
    dispatch(PlansActions.plansReactivateRequest());
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pb-24 md:pb-8">
      {/* Success Banner */}
      {successBanner && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-2 text-green-800 text-sm font-medium">
            <span>✓</span> {successBanner}
          </div>
          <button onClick={() => setSuccessBanner(null)} className="text-green-600 hover:text-green-800">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-8">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm font-medium mb-4"
        >
          ← Back to Dashboard
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Choose Your Plan</h1>
        <p className="text-gray-500">
          Start free, upgrade when you're ready
        </p>
      </div>

      {isFetching ? (
        <div className="flex items-center justify-center py-16">
          <Spinner size="xl" />
        </div>
      ) : (
        <>
          {/* Canceling notice */}
          {isSubscriptionCanceling && formattedEndsAt && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    Your subscription is set to cancel on {formattedEndsAt}
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    You'll keep Creator Pro access until then. After that, your plan will revert to Free.
                  </p>
                </div>
                <button
                  onClick={handleReactivate}
                  disabled={isReactivating}
                  className="px-3 py-1.5 text-xs font-medium text-amber-800 bg-amber-100 rounded-lg hover:bg-amber-200 transition-colors disabled:opacity-50 flex-shrink-0 ml-4"
                >
                  {isReactivating ? 'Reactivating...' : 'Undo Cancellation'}
                </button>
              </div>
            </div>
          )}

          {/* Billing cycle toggle */}
          {!isCurrentPlan && (
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-400'}`}>Monthly</span>
              <button
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${billingCycle === 'annual' ? 'bg-primary-500' : 'bg-gray-300'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${billingCycle === 'annual' ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <span className={`text-sm font-medium ${billingCycle === 'annual' ? 'text-gray-900' : 'text-gray-400'}`}>
                Annual
                <span className="ml-1 text-xs text-green-600 font-semibold">Save 17%</span>
              </span>
            </div>
          )}

          {/* Free vs Pro comparison */}
          {!isCurrentPlan && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Free</h3>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-gray-900">$0</span>
                  <span className="text-gray-500">/month</span>
                </div>
                <ul className="space-y-2.5 text-sm">
                  <li className="flex items-center gap-2 text-gray-600">
                    <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                    30 credits/month
                  </li>
                  <li className="flex items-center gap-2 text-gray-600">
                    <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                    All features
                  </li>
                  <li className="flex items-center gap-2 text-gray-400">
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
                    Watermarked
                  </li>
                </ul>
                {currentPlan !== 'creator_pro' && !isSubscriptionCanceling && (
                  <div className="mt-4 px-4 py-2.5 text-sm font-medium text-gray-500 bg-gray-50 rounded-lg text-center">
                    Current Plan
                  </div>
                )}
              </div>
              <div className="bg-white rounded-2xl border-2 border-primary-500 p-6 relative shadow-lg">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-primary-500 text-white">
                    Most Popular
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1 mt-1">Creator Pro</h3>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-gray-900">${monthlyPrice}</span>
                  <span className="text-gray-500">/month</span>
                </div>
                <ul className="space-y-2.5 text-sm">
                  <li className="flex items-center gap-2 text-gray-600">
                    <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                    <strong>200</strong> credits/month
                  </li>
                  <li className="flex items-center gap-2 text-gray-600">
                    <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                    All features
                  </li>
                  <li className="flex items-center gap-2 text-gray-600">
                    <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                    <strong>No watermark</strong>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Main plan card (detailed) */}
          <div className={`relative bg-white rounded-2xl border-2 p-8 mb-8 ${
            isCurrentPlan ? "border-green-500" : "border-primary-500 shadow-lg"
          }`}>
            {isCurrentPlan && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-500 text-white">
                  {isSubscriptionCanceling ? 'Canceling' : 'Your Current Plan'}
                </span>
              </div>
            )}

            <div className="text-center mb-6 mt-2">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">{creatorProPlan.name || 'Creator Pro'}</h2>
              <div className="mt-3">
                <span className="text-4xl font-bold text-gray-900">${monthlyPrice}</span>
                <span className="text-gray-500 text-lg">/month</span>
                {billingCycle === 'annual' && (
                  <p className="text-sm text-green-600 mt-1">
                    ${totalPrice}/year (save $58 vs monthly)
                  </p>
                )}
              </div>
            </div>

            {/* Credits highlight */}
            <div className="text-center mb-6 py-4 bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl">
              <span className="text-3xl font-bold text-primary-600">200</span>
              <span className="text-gray-600 text-sm block mt-1">credits per month</span>
              <p className="text-sm text-primary-700 font-medium mt-2">~20 complete animated videos/month</p>
              <p className="text-xs text-gray-500 mt-1">Just $1.45 per video vs $50-200 for a video editor</p>
            </div>

            {/* Credit usage bar (only show if subscribed) */}
            {isCurrentPlan && (
              <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Credits Remaining</span>
                  <span className="font-medium text-gray-900">
                    {availableCredits} of {monthlyCredits} credits left
                  </span>
                </div>
                <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      usagePercent < 20 ? "bg-red-500" : usagePercent < 50 ? "bg-amber-500" : "bg-green-500"
                    }`}
                    style={{ width: `${Math.min(usagePercent, 100)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Features */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              {(creatorProPlan.features || FALLBACK_FEATURES).map((feature, index) => (
                <div key={index} className="flex items-start gap-2 text-sm">
                  <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                  <span className="text-gray-600">{feature}</span>
                </div>
              ))}
            </div>

            {/* CTA / Status */}
            {isCurrentPlan && !isSubscriptionCanceling ? (
              <button
                disabled
                className="w-full px-4 py-3 text-sm font-medium text-green-700 bg-green-50 rounded-lg cursor-default"
              >
                ✓ Active Plan
              </button>
            ) : isSubscriptionCanceling ? (
              <button
                onClick={handleReactivate}
                disabled={isReactivating}
                className="w-full px-4 py-3 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
              >
                {isReactivating ? 'Reactivating...' : 'Reactivate Subscription'}
              </button>
            ) : (
              <button
                onClick={handleSubscribeClick}
                disabled={isSubscribing}
                className="w-full px-4 py-3 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
              >
                {isSubscribing ? 'Redirecting to checkout...' : billingCycle === 'annual' ? 'Subscribe Now — $290/year' : 'Subscribe Now — $29/month'}
              </button>
            )}

            {/* Error messages */}
            {subscribeError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
                <p className="text-sm text-red-600 flex-1">{subscribeError}</p>
                <button onClick={handleSubscribeClick} className="text-xs font-medium text-red-600 hover:text-red-800 underline flex-shrink-0">
                  Try again
                </button>
              </div>
            )}
            {downgradeError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
                <p className="text-sm text-red-600">{downgradeError}</p>
              </div>
            )}
          </div>

          {/* Credit cost breakdown */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">How Credits Work</h3>
            <p className="text-sm text-gray-500 mb-4">
              Simple pricing: 1 credit = 1 generation. No hidden costs.
            </p>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <span className="text-lg" aria-hidden="true">🖼️</span>
                  <span className="text-sm text-gray-700">Image Generation (per scene)</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">1 credit</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <span className="text-lg" aria-hidden="true">🎬</span>
                  <span className="text-sm text-gray-700">Animation Generation (per scene)</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">1 credit</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <span className="text-lg" aria-hidden="true">🔊</span>
                  <span className="text-sm text-gray-700">Audio/TTS Generation</span>
                </div>
                <span className="text-sm font-semibold text-gray-900">1 credit</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg" aria-hidden="true">📝</span>
                  <span className="text-sm text-gray-700">Subtitles</span>
                </div>
                <span className="text-sm font-semibold text-green-600">Free</span>
              </div>
            </div>
          </div>

          {/* Example usage */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">What Can You Create?</h3>
            <p className="text-sm text-gray-500 mb-4">
              With 200 credits per month, here's what you can do:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-blue-50 rounded-xl text-center">
                <span className="text-2xl font-bold text-blue-600">~40</span>
                <p className="text-xs text-gray-600 mt-1">videos (5 scenes, no animation)</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-xl text-center">
                <span className="text-2xl font-bold text-purple-600">~20</span>
                <p className="text-xs text-gray-600 mt-1">videos (5 scenes, animated)</p>
              </div>
              <div className="p-4 bg-green-50 rounded-xl text-center">
                <span className="text-2xl font-bold text-green-600">~10</span>
                <p className="text-xs text-gray-600 mt-1">videos (10 scenes, animated)</p>
              </div>
            </div>
          </div>

          {/* Why Creator Pro */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Why Creator Pro?</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                <div>
                  <span className="font-medium text-gray-900">Original AI visuals, not stock footage</span>
                  <p className="text-gray-500 mt-0.5">Unlike InVideo which assembles stock clips, every visual is AI-generated and unique to your script</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                <div>
                  <span className="font-medium text-gray-900">Full pipeline in one tool</span>
                  <p className="text-gray-500 mt-0.5">Script + AI images + animation + voiceover + subtitles + export — no need for 5 separate tools</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                <div>
                  <span className="font-medium text-gray-900">$1.45/video vs $50-200 for a video editor</span>
                  <p className="text-gray-500 mt-0.5">Create 20 videos for the price of one freelance edit</p>
                </div>
              </div>
            </div>
          </div>

          {/* Billing behavior */}
          <div className="bg-gray-50 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Billing</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-gray-400 mt-0.5">•</span>
                Credits reset monthly (no rollover)
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gray-400 mt-0.5">•</span>
                Usage bar displayed in your dashboard
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gray-400 mt-0.5">•</span>
                Generation blocked when credits reach 0
              </li>
            </ul>
          </div>

          {/* Cancel / Downgrade section */}
          {isCurrentPlan && !isSubscriptionCanceling && (
            <div className="mt-6 p-4 border border-gray-200 rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">Cancel Subscription</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    You'll keep access until the end of your billing period
                  </p>
                </div>
                <button
                  onClick={handleDowngradeClick}
                  disabled={isDowngrading}
                  className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                >
                  {isDowngrading ? 'Canceling...' : 'Cancel Plan'}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Credit Top-Up Packs */}
      <div className="mt-8 bg-white rounded-2xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Need More Credits?</h3>
        <p className="text-sm text-gray-500 mb-4">
          Buy additional credits anytime. Top-up credits never expire and are used after your monthly credits.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { id: 'pack_50', credits: 50, price: '$7.50', perCredit: '$0.15' },
            { id: 'pack_100', credits: 100, price: '$12.00', perCredit: '$0.12', popular: true },
            { id: 'pack_200', credits: 200, price: '$20.00', perCredit: '$0.10' },
          ].map((pack) => (
            <button
              key={pack.id}
              onClick={() => dispatch(PlansActions.plansBuyCreditsRequest(pack.id, window.location.origin))}
              className={`relative p-4 rounded-xl border-2 text-center transition-colors hover:border-primary-400 hover:bg-primary-50 ${
                pack.popular ? 'border-primary-300 bg-primary-50/30' : 'border-gray-200'
              }`}
            >
              {pack.popular && (
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-semibold bg-primary-500 text-white px-2 py-0.5 rounded-full">
                  Best Value
                </span>
              )}
              <span className="text-2xl font-bold text-gray-900 block">{pack.credits}</span>
              <span className="text-xs text-gray-500 block">credits</span>
              <span className="text-lg font-semibold text-primary-600 block mt-2">{pack.price}</span>
              <span className="text-[10px] text-gray-400">{pack.perCredit}/credit</span>
            </button>
          ))}
        </div>
      </div>

      {/* Contact */}
      <div className="mt-8 text-center">
        <p className="text-gray-500 text-sm">
          Questions about credits or billing?
        </p>
        <a
          href="mailto:support@prompttovid.com"
          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
        >
          support@prompttovid.com
        </a>
      </div>

      {/* Sticky mobile CTA */}
      {!isCurrentPlan && !isFetching && (
        <div className="fixed bottom-0 left-0 right-0 md:hidden bg-white border-t border-gray-200 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] z-40">
          <button
            onClick={handleSubscribeClick}
            disabled={isSubscribing}
            className="w-full px-4 py-3 text-sm font-semibold text-white bg-primary-500 rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50 shadow-lg"
          >
            {isSubscribing ? 'Redirecting...' : `Upgrade to Pro — $${monthlyPrice}/mo`}
          </button>
        </div>
      )}

      {/* Downgrade Confirmation Modal */}
      {showDowngradeModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowDowngradeModal(false)} />
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl p-6 w-full sm:max-w-md sm:mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Cancel Subscription?</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to cancel your Creator Pro subscription?
            </p>
            <div className="bg-red-50 rounded-lg p-3 mb-3">
              <p className="text-xs font-medium text-red-800 mb-2">You'll lose:</p>
              <ul className="space-y-1 text-sm text-red-700">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">✕</span>
                  200 credits/month (down to 30)
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">✕</span>
                  Watermark-free exports
                </li>
              </ul>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 mb-4">
              <ul className="space-y-1.5 text-sm text-amber-800">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">•</span>
                  You'll keep access until the end of your current billing period
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">•</span>
                  You can reactivate anytime before the period ends
                </li>
              </ul>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDowngradeModal(false)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors"
              >
                Keep Plan
              </button>
              <button
                onClick={handleConfirmDowngrade}
                disabled={isDowngrading}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
              >
                {isDowngrading ? 'Canceling...' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const FALLBACK_FEATURES = [
  '200 credits/month',
  '1 credit = 1 generation (Image, Video, or Audio)',
  'Full access to all features',
  'No watermark on videos',
  '1080p/4K export',
  'All generation types',
];

// Fallback plans data when API is not available
const FALLBACK_PLANS = [
  {
    key: 'free',
    name: 'Free',
    monthly_credits: 30,
    price_monthly: 0,
    price_yearly: 0,
    features: ['30 credits/month', 'Try all features', 'Watermarked videos'],
    limits: { videos_per_month: -1, max_video_duration: -1, team_members: 1 },
    is_current: false,
    can_upgrade: true,
  },
  {
    key: 'creator_pro',
    name: 'Creator Pro',
    monthly_credits: 200,
    price_monthly: 29,
    price_yearly: 290,
    features: FALLBACK_FEATURES,
    limits: { videos_per_month: -1, max_video_duration: -1, team_members: 1 },
    is_current: false,
    can_upgrade: true,
  },
];
